import { getBillTos, GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView, getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToCollectionModel, BillToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { ValidatorRuleType } from "@tKgpco/Common/Utilities/validate";
import MultiSelectDropdown, { Option } from "@tKgpco/Components/MultiSelectDropdown";
import { useForm } from "@tKgpco/Custom/Hooks/useForm";
import addNewAccessorial from "@tKgpco/Handlers/Accessorials/AddAccessorial";
import { AddAccessorialApiParameters } from "@tKgpco/Services/AccessorialsService";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import {
    FeatureRolePermissionsProperty,
    getOOTBPageDetailsHn,
    getOOTBWidgetDetailsHn,
    getPageDetailsHn,
    getPagesWidget,
    getPagesWidgetHn,
    PageDetail,
    WidgetDetail,
} from "@tKgpco/Services/SecurityRolesService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import updateRole from "@tKgpco/Store/Data/SecurityRoles/Handlers/AddRole";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";

export interface AddGetBillTosParameter extends GetBillTosApiParameter {
    allCustomers?: boolean;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { getBillTosParameter } = state.pages.rfqMyQuotes;
    return {
        // billTosDataView: getBillTosDataView(state, {}),
        billTosDataView:
            getBillTosParameter &&
            getBillTosDataView(state, {
                ...getBillTosParameter,
                allCustomers: true,
                filter: "",
            } as AddGetBillTosParameter),
        parameter: state.pages.orderHistory.getOrdersParameter,
        contextBillTo: state.context.session.billToId,
        settings: getSettingsCollection(state),
        getBillTosParameter,
        billToState: getBillToState(state, state.context.session.billToId),
    };
};

const mapDispatchToProps = {
    updateRole,
    loadBillTos,
};

interface CustomWidgetStyles {
    mainGridContainer?: GridContainerProps;
    heading?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    mainGridContainer: {
        offsetCss: css`
            max-width: 700px;
            margin: 0 auto;
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                );
            }}
        `,
        css: css`
            .dropdown {
                [class^="StyledButton"] {
                    background-color: #fff;
                }
            }
        `,
    },
    heading: {
        css: css`
            text-transform: uppercase;
            font-size: 22px;
            color: #0b70b7;
            border-bottom: 1px solid #0b70b7;
            padding-bottom: 5px;
            width: 100%;
        `,
    },
};

const EMPTY_STRING = "";

interface AccessorialsFormValues {
    name: string;
}

const formValues: AccessorialsFormValues = {
    name: EMPTY_STRING,
};

const initialErrorState: Omit<AccessorialsFormValues, "enabled" | "carrierNoteOnly"> = {
    name: EMPTY_STRING,
};

const validatorConfig: { [key in keyof AccessorialsFormValues]: ValidatorRuleType } = {
    name: {
        required: true,
        label: "Name",
        requiredMsg: "Name is required",
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const AddSecurityRoles: React.FC<Props> = ({
    id,
    updateRole,
    history,
    billToState,
    billTosDataView,
    contextBillTo,
    getBillTosParameter,
    loadBillTos,
}) => {
    const {
        fieldChangeHandler,
        validateField,
        updateFormState,
        updateFormErrors,
        formState,
        formErrors,
        isSubmitDisabled,
    } = useForm({
        formValues,
        initialErrorState,
        validatorConfig,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const [pageDetails, setPageDetails] = useState<PageDetail[]>();
    const [ootbPageDetails, setOOTBPageDetails] = useState<PageDetail[]>();
    const [widgetDetails, setWidgetDetails] = useState<WidgetDetail[]>();
    const [ootbWidgetDetails, setOOTBWidgetDetails] = useState<WidgetDetail[]>();

    const [selectedPages, setSelectedPages] = useState<Option[]>([]);
    const [selectedOOTBPages, setSelectedOOTBPages] = useState<Option[]>([]);
    const [selectedWidgets, setSelectedWidgets] = useState<Option[]>([]);
    const [selectedOOTBWidgets, setSelectedOOTBWidgets] = useState<Option[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<Option[]>([]);
    const [billToCollection, setBillToCollection] = useState<BillToModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const isNotValid = formState.name.trim() === "" || !selectedCustomers.length;

    useEffect(() => {
        getPageDetailsHn().then(response => {
            setPageDetails(response.customPageDetails ?? []);
            // setWidgetDetails(response.customWidgetDetails ?? []);
        });

        getOOTBPageDetailsHn().then(response => {
            setOOTBPageDetails(response.oobPageDetails ?? []);
        });
    }, []);

    useEffect(() => {
        if (selectedPages.length) {
            getPagesWidgetHn({ pageName: selectedPages.map(page => page.label).join(",") || "" }).then(response => {
                setWidgetDetails(response.customWidgetDetails ?? []);
                const widgetOptions = response.customWidgetDetails?.map(widget => widget.featureSubPage).join(",");
                const filteredWidgetOptions = selectedWidgets.filter(widget => widgetOptions.includes(widget.label));
                setSelectedWidgets(filteredWidgetOptions ?? []);
            });
        }
    }, [selectedPages]);

    useEffect(() => {
        if (selectedOOTBPages.length) {
            getOOTBWidgetDetailsHn({ pageName: selectedOOTBPages.map(page => page.label).join(",") || "" }).then(
                response => {
                    setOOTBWidgetDetails(response.oobWidgetDetails ?? []);
                    const widgetOptions = response.oobWidgetDetails?.map(widget => widget.featureSubPage).join(",");
                    const filteredWidgetOptions = selectedOOTBWidgets.filter(widget =>
                        widgetOptions.includes(widget.label),
                    );
                    setSelectedOOTBWidgets(filteredWidgetOptions ?? []);
                },
            );
        }
    }, [selectedOOTBPages]);

    React.useEffect(() => {
        setIsLoading(true);
        getBillTos({ ...getBillTosParameter, allCustomers: true } as AddGetBillTosParameter).then(response => {
            setBillToCollection(response.billTos || []);
            setIsLoading(false);
        });
    }, []);

    const createRole = () => {
        setIsSubmitting(true);

        const allSelectedPage = [...(selectedPages ?? []), ...(selectedOOTBPages ?? [])];
        const allSelectedWidget = [...(selectedWidgets ?? []), ...(selectedOOTBWidgets ?? [])];

        const params: FeatureRolePermissionsProperty = {
            isEdit: false,
            roleName: formState.name,
            customerNumber: selectedCustomers.map(customer => customer.label).join(","),
            featureDetails:
                allSelectedWidget.length || allSelectedPage.length
                    ? [
                          {
                              featureName: allSelectedPage.map(page => page.label).join(",") || "",
                              featureSubPage: allSelectedWidget.map(widget => widget.label).join(",") || "",
                          },
                      ]
                    : [],
        };
        updateRole({
            ...params,
            onSuccess: (result: any) => {
                setIsSubmitting(false);
                updateFormState(formValues);
                updateFormErrors(initialErrorState);
                history.push("/MyAccount/SecurityRoles");
                toasterContext.addToast({
                    messageType: "success",
                    body: translate(`${result.message}.`),
                    timeoutLength: 4000,
                });
            },
        });
    };

    // For Pages
    const optionsPages = pageDetails || [];
    const uniquePageOptions = new Map<string, string[]>();
    for (const option of optionsPages) {
        const securityPagesOption = uniquePageOptions.get(option.featureName) || [];
        securityPagesOption.push(option.id);
        uniquePageOptions.set(option.featureName, securityPagesOption);
    }

    const dropDownOptionsPages: Option[] = Array.from(uniquePageOptions, ([name, values]) => ({ name, values }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptionsPages: Option[] =
        selectedPages && selectedPages.length > 0
            ? selectedPages.map(val => ({
                  value: val.value,
                  label: dropDownOptionsPages.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];

    const handleDropdownChangePages = (option: Option) => {
        const isSelected =
            selectedPages &&
            selectedPages.length > 0 &&
            selectedPages.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedPages && selectedPages.length > 0 ? [...selectedPages] : [];
        if (isSelected) {
            status = selectedPages!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedPages(status);
        } else {
            setSelectedPages([]);
            setSelectedWidgets([]);
        }
    };

    // For OOTB Pages
    const optionsOOTBPages = ootbPageDetails || [];
    const uniqueOOTBPageOptions = new Map<string, string[]>();
    for (const option of optionsOOTBPages) {
        const securityPagesOption = uniqueOOTBPageOptions.get(option.featureName) || [];
        securityPagesOption.push(option.id);
        uniqueOOTBPageOptions.set(option.featureName, securityPagesOption);
    }

    const dropDownOptionsOOTBPages: Option[] = Array.from(uniqueOOTBPageOptions, ([name, values]) => ({ name, values }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptionsOOTBPages: Option[] =
        selectedOOTBPages && selectedOOTBPages.length > 0
            ? selectedOOTBPages.map(val => ({
                  value: val.value,
                  label: dropDownOptionsOOTBPages.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];

    const handleDropdownChangeOOTBPages = (option: Option) => {
        const isSelected =
            selectedOOTBPages &&
            selectedOOTBPages.length > 0 &&
            selectedOOTBPages.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedOOTBPages && selectedOOTBPages.length > 0 ? [...selectedOOTBPages] : [];
        if (isSelected) {
            status = selectedOOTBPages!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedOOTBPages(status);
        } else {
            setSelectedOOTBPages([]);
            setSelectedOOTBWidgets([]);
        }
    };

    // For Widgets

    const optionsWidgets = widgetDetails || [];
    const uniqueWidgetOptions = new Map<string, string[]>();
    for (const option of optionsWidgets) {
        const securityWidgetsOption = uniqueWidgetOptions.get(option.featureSubPage) || [];
        securityWidgetsOption.push(option.id);
        uniqueWidgetOptions.set(option.featureSubPage, securityWidgetsOption);
    }

    const dropDownOptionsWidgets: Option[] = Array.from(uniqueWidgetOptions, ([name, values]) => ({ name, values }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptionsWidgets: Option[] =
        selectedWidgets && selectedWidgets.length > 0
            ? selectedWidgets.map(val => ({
                  value: val.value,
                  label: dropDownOptionsWidgets.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];

    const handleDropdownChangeWidgets = (option: Option) => {
        const isSelected =
            selectedWidgets &&
            selectedWidgets.length > 0 &&
            selectedWidgets.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedWidgets && selectedWidgets.length > 0 ? [...selectedWidgets] : [];
        if (isSelected) {
            status = selectedWidgets!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedWidgets(status);
        } else {
            setSelectedWidgets([]);
        }
    };

    // For OOTB Widgets

    const ootbOptionsWidgets = ootbWidgetDetails || [];
    const uniqueOOTBWidgetOptions = new Map<string, string[]>();
    for (const option of ootbOptionsWidgets) {
        const securityWidgetsOption = uniqueOOTBWidgetOptions.get(option.featureSubPage) || [];
        securityWidgetsOption.push(option.id);
        uniqueOOTBWidgetOptions.set(option.featureSubPage, securityWidgetsOption);
    }

    const dropDownOptionsOOTBWidgets: Option[] = Array.from(uniqueOOTBWidgetOptions, ([name, values]) => ({
        name,
        values,
    }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptionsOOTBWidgets: Option[] =
        selectedOOTBWidgets && selectedOOTBWidgets.length > 0
            ? selectedOOTBWidgets.map(val => ({
                  value: val.value,
                  label: dropDownOptionsOOTBWidgets.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];

    const handleDropdownChangeOOTBWidgets = (option: Option) => {
        const isSelected =
            selectedOOTBWidgets &&
            selectedOOTBWidgets.length > 0 &&
            selectedOOTBWidgets.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedOOTBWidgets && selectedOOTBWidgets.length > 0 ? [...selectedOOTBWidgets] : [];
        if (isSelected) {
            status = selectedOOTBWidgets!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedOOTBWidgets(status);
        } else {
            setSelectedOOTBWidgets([]);
        }
    };

    // For Customers

    const optionsCustomer = billToCollection || [];
    const uniqueCustomerOptions = new Map<string, string[]>();
    for (const option of optionsCustomer) {
        const customerOption = uniqueCustomerOptions.get(option.customerNumber) || [];
        customerOption.push(option.id);
        uniqueCustomerOptions.set(option.customerNumber, customerOption);
    }

    const dropDownOptionsCustomers: Option[] = Array.from(uniqueCustomerOptions, ([name, values]) => ({ name, values }))
        .map(option => {
            let opt = "";
            if (option.name.length === 1 && (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name;
            }
            return {
                value: option.values.join(","),
                label: opt,
            };
        })
        .filter(a => a.label !== "")
        .sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptionsCustomers: Option[] =
        selectedCustomers && selectedCustomers.length > 0
            ? selectedCustomers.map(val => ({
                  value: val.value,
                  // label: dropDownOptionsCustomers.find(opt => opt.value === val.value)?.label || "",
                  label: val.label || "",
              }))
            : [];

    const handleDropdownChangeCustomers = (option: Option) => {
        const isSelected =
            selectedCustomers &&
            selectedCustomers.length > 0 &&
            selectedCustomers.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedCustomers && selectedCustomers.length > 0 ? [...selectedCustomers] : [];
        if (isSelected) {
            status = selectedCustomers!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }

        if (status.length > 0) {
            setSelectedCustomers(status);
        } else {
            setSelectedCustomers([]);
        }
    };

    const handleSearchChange = (searchText: string) => {
        getBillTos({
            ...getBillTosParameter,
            allCustomers: true,
            filter: searchText,
        } as AddGetBillTosParameter).then(response => {
            setBillToCollection(response.billTos || []);
        });
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem width={12}>
                    <Typography as="h2" {...styles.heading}>
                        {translate("Add New Security Role")}
                    </Typography>
                </GridItem>

                <GridItem width={12}>
                    <TextField
                        label={translate("Name")}
                        name="name"
                        value={formState.name}
                        required
                        onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    />
                </GridItem>
                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Customer *")}
                        handleOptionClick={handleDropdownChangeCustomers}
                        options={dropDownOptionsCustomers}
                        selectedOptions={selectedOptionsCustomers}
                        placeHolder={translate("Select Customers")}
                        isSearchEnabled={true}
                        handleSearchChange={handleSearchChange}
                        searchPlaceHolder={translate("Search Customers")}
                    />
                </GridItem>
                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Show Pages")}
                        handleOptionClick={handleDropdownChangePages}
                        options={dropDownOptionsPages}
                        selectedOptions={selectedOptionsPages}
                        placeHolder={translate("Select Pages")}
                        tooltipText={siteMessage("SecurityRoles_ShowPagesTooltip") as string}
                    />
                </GridItem>
                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Show Widgets")}
                        handleOptionClick={handleDropdownChangeWidgets}
                        options={dropDownOptionsWidgets}
                        selectedOptions={selectedOptionsWidgets}
                        placeHolder={translate("Select Widgets")}
                        isDisabled={!selectedPages.length}
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Hide Pages")}
                        handleOptionClick={handleDropdownChangeOOTBPages}
                        options={dropDownOptionsOOTBPages}
                        selectedOptions={selectedOptionsOOTBPages}
                        placeHolder={translate("Select OOTB Pages")}
                        tooltipText={siteMessage("SecurityRoles_HideOOTBPagesTooltip") as string}
                    />
                </GridItem>
                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Hide Widgets")}
                        handleOptionClick={handleDropdownChangeOOTBWidgets}
                        options={dropDownOptionsOOTBWidgets}
                        selectedOptions={selectedOptionsOOTBWidgets}
                        placeHolder={translate("Select OOTB Widgets")}
                    />
                </GridItem>

                <GridItem>
                    <Button onClick={createRole} disabled={isNotValid || isSubmitting}>
                        Submit
                    </Button>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const addSecurityRolesWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(AddSecurityRoles)),
    definition: {
        group: "Basic",
    },
};

export default addSecurityRolesWidget;
